import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import CloudImagePicker from "@/components/admin/CloudImagePicker";
import MultiImagePicker from "@/components/admin/MultiImagePicker";
import { fileUrl } from "@/lib/api";
import { getWedding, normalizeGallery, normalizeWedding, saveWedding } from "@/lib/cloudApi";

const empty = {
  slug: "", couple: "", location: "", type: "Wedding",
  cover: "", story: "", fullStory: "", gallery: [], videoUrl: "", order: 0,
  coverPositionDesktop: "center center",
  coverPositionMobile: "center center",
  coverFit: "cover",
};

const coverPositions = [
  ["Top left", "left top"],
  ["Top center", "center top"],
  ["Top right", "right top"],
  ["Center left", "left center"],
  ["Center center", "center center"],
  ["Center right", "right center"],
  ["Bottom left", "left bottom"],
  ["Bottom center", "center bottom"],
  ["Bottom right", "right bottom"],
];

export default function AdminWeddingForm() {
  const { slug } = useParams();
  const editing = !!slug;
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [loading, setLoading] = useState(editing);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const data = await getWedding(slug);
        if (data) {
          const wedding = normalizeWedding(data);
          setForm({ ...empty, ...wedding, gallery: normalizeGallery(wedding.gallery), videoUrl: wedding.videoUrl || "" });
        }
      } catch (e) { toast.error("Failed to load"); nav("/admin/weddings"); }
      finally { setLoading(false); }
    })();
  }, [slug, editing, nav]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (galleryUploading) {
      toast.error("Please wait for the gallery upload to finish");
      return;
    }
    if (!form.slug.match(/^[a-z0-9-]+$/)) {
      toast.error("Slug must be lowercase letters/numbers/hyphens only");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        gallery: normalizeGallery(form.gallery),
        videoUrl: form.videoUrl || null,
        order: Number(form.order) || 0,
      };
      await saveWedding({ ...payload, ...(editing ? { slug } : {}) });
      toast.success(editing ? "Updated" : "Created");
      nav("/admin/weddings");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const input = "w-full bg-transparent border-b border-[#C99A45]/30 focus:border-[#C99A45] outline-none py-2 text-sm";

  if (loading) return <div className="p-10 text-[#F7F1E8]/60">Loading...</div>;

  return (
    <div className="p-10 max-w-4xl" data-testid="wedding-form">
      <Link to="/admin/weddings" className="eyebrow text-[10px] text-[#C99A45] inline-flex items-center gap-2 hover:underline">
        <ArrowLeft size={12} /> Back
      </Link>
      <h1 className="font-serif-display text-4xl mt-3">{editing ? "Edit" : "Add"} Wedding Story</h1>
      <div className="gold-divider my-6"></div>

      <form onSubmit={submit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="eyebrow text-[10px] text-[#C99A45]/80">Couple *</label>
            <input required value={form.couple} onChange={(e) => set("couple", e.target.value)} className={input} data-testid="w-couple" />
          </div>
          <div>
            <label className="eyebrow text-[10px] text-[#C99A45]/80">Slug (URL) *</label>
            <input required value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} placeholder="aarav-nidhi" className={input} data-testid="w-slug" />
          </div>
          <div>
            <label className="eyebrow text-[10px] text-[#C99A45]/80">Location *</label>
            <input required value={form.location} onChange={(e) => set("location", e.target.value)} className={input} data-testid="w-location" />
          </div>
          <div>
            <label className="eyebrow text-[10px] text-[#C99A45]/80">Type *</label>
            <select value={form.type} onChange={(e) => set("type", e.target.value)} className={`${input} bg-[#070707]`} data-testid="w-type">
              {["Wedding", "Pre-Wedding", "Engagement", "Destination Wedding", "Haldi / Mehendi", "Reception"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow text-[10px] text-[#C99A45]/80">Display Order</label>
            <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className={input} data-testid="w-order" />
          </div>
          <div>
            <label className="eyebrow text-[10px] text-[#C99A45]/80">Film URL (optional)</label>
            <input value={form.videoUrl} onChange={(e) => set("videoUrl", e.target.value)} placeholder="https://player.vimeo.com/video/..." className={input} data-testid="w-video" />
          </div>
        </div>

        <div>
          <label className="eyebrow text-[10px] text-[#C99A45]/80">Short Story (cards) *</label>
          <textarea required rows={2} value={form.story} onChange={(e) => set("story", e.target.value)} className={`${input} resize-none`} data-testid="w-story" />
        </div>
        <div>
          <label className="eyebrow text-[10px] text-[#C99A45]/80">Full Story (detail page)</label>
          <textarea rows={4} value={form.fullStory} onChange={(e) => set("fullStory", e.target.value)} className={`${input} resize-none`} data-testid="w-fullstory" />
        </div>

        <div className="space-y-4">
          <CloudImagePicker
            value={form.cover}
            onChange={(v) => set("cover", v)}
            label="Cover Image *"
            testId="w-cover"
            folder={`weddings/${form.slug || "new"}/cover`}
            previewClassName="wedding-cover-image"
            previewStyle={{
              "--cover-position-desktop": form.coverPositionDesktop,
              "--cover-position-mobile": form.coverPositionMobile,
              "--cover-fit": form.coverFit,
            }}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="eyebrow text-[10px] text-[#C99A45]/80">Cover desktop position</label>
              <select
                value={form.coverPositionDesktop}
                onChange={(e) => set("coverPositionDesktop", e.target.value)}
                className={`${input} bg-[#070707]`}
                data-testid="w-cover-position-desktop"
              >
                {coverPositions.map(([label, value]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow text-[10px] text-[#C99A45]/80">Cover mobile position</label>
              <select
                value={form.coverPositionMobile}
                onChange={(e) => set("coverPositionMobile", e.target.value)}
                className={`${input} bg-[#070707]`}
                data-testid="w-cover-position-mobile"
              >
                {coverPositions.map(([label, value]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow text-[10px] text-[#C99A45]/80">Cover fit</label>
              <select
                value={form.coverFit}
                onChange={(e) => set("coverFit", e.target.value)}
                className={`${input} bg-[#070707]`}
                data-testid="w-cover-fit"
              >
                <option value="cover">Cover</option>
                <option value="contain">Contain</option>
              </select>
            </div>
          </div>

          {form.cover && (
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4" data-testid="w-cover-framing-preview">
              <div>
                <div className="eyebrow text-[9px] text-[#C99A45]/60 mb-2">Desktop preview</div>
                <div className="aspect-video bg-[#151515] overflow-hidden border border-[#C99A45]/20">
                  <img
                    src={fileUrl(form.cover)}
                    alt="Desktop cover framing preview"
                    className="w-full h-full"
                    style={{ objectFit: form.coverFit, objectPosition: form.coverPositionDesktop }}
                  />
                </div>
              </div>
              <div>
                <div className="eyebrow text-[9px] text-[#C99A45]/60 mb-2">Mobile preview</div>
                <div className="aspect-[4/5] bg-[#151515] overflow-hidden border border-[#C99A45]/20 max-w-[220px]">
                  <img
                    src={fileUrl(form.cover)}
                    alt="Mobile cover framing preview"
                    className="w-full h-full"
                    style={{ objectFit: form.coverFit, objectPosition: form.coverPositionMobile }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <MultiImagePicker
          urls={form.gallery}
          onChange={(v) => set("gallery", normalizeGallery(v))}
          onUploadingChange={setGalleryUploading}
          label="Gallery"
          testId="w-gallery"
          folder={`weddings/${form.slug || "new"}/gallery`}
          allowDriveFolderImport
        />

        <div className="flex gap-3 pt-4">
          <button type="submit" disabled={busy || galleryUploading} className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed" data-testid="w-save">
            {busy || galleryUploading ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {galleryUploading ? "Uploading Gallery..." : editing ? "Update" : "Create"}
          </button>
          <Link to="/admin/weddings" className="btn-outline-gold">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
